"use client";

import {
  creditSlateForDurationDays,
  Eligibility,
  LockDurationDays,
  TrialDto,
  trialSizeFor,
  USDC_SCALE,
} from "@decibeltrade/sdk";
import { useQueryClient } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useMemo, useRef, useState } from "react";
import { flex } from "styled-system/patterns";

import { isLockDurationDays } from "@/app/(main)/rewards/first-trade-on-us/fft-flow-config";
import { useFftFlowStore } from "@/app/(main)/rewards/first-trade-on-us/hooks/use-fft-flow-store";
import { toast } from "@/components/toast";
import { useAnalytics } from "@/contexts/analytics-context";
import { readDexSdk, sdkConfig } from "@/contexts/dex-sdk";
import { useClaimUnlock } from "@/contexts/mutations/funded-trial/claim-unlock";
import { useLockUsdc } from "@/contexts/mutations/funded-trial/lock";
import { useOpenTrial } from "@/contexts/mutations/funded-trial/open-trial";
import { useSettleTrial } from "@/contexts/mutations/funded-trial/settle-trial";
import { writeActiveTrial } from "@/contexts/queries/funded-trial/active-trial";
import { fundedTrialEligibilityQueryKey } from "@/contexts/queries/funded-trial/eligibility";
import { useTrialHistoryInfiniteQuery } from "@/contexts/queries/funded-trial/trial-history";
import { useAllMarketPricesQuery } from "@/contexts/queries/prices";
import { useLockCountdown } from "@/hooks/use-lock-countdown";
import type { FftTrialOpenSource } from "@/lib/analytics";
import {
  isFftLockHasOpenOrdersError,
  isFftLockHasOpenPositionError,
  isFftLockWithdrawalQueuedError,
  isFftRelockDisabledError,
} from "@/utils/fft-errors";
import { Logger } from "@/utils/logger";
import { NumberFormatter } from "@/utils/number-formatter";
import { getServerTime } from "@/utils/server-time";
import { MS_PER_DAY } from "@/utils/time";

import {
  deriveFftStep,
  type FftStep,
  isAwaitingOpen,
  type PendingOpen,
  resolveFftStep,
  trialKey,
} from "./derive-fft-step";
import { deriveProfitsKeptUsd } from "./derive-profits-kept";
import { DetailsBodyVariant, DetailsPane, preloadFftTrialChart } from "./details-pane";
import { FftCreditsUsedAlert } from "./fft-credits-used-alert";
import { FftMarketClosedAlert } from "./fft-market-closed-alert";
import { LeversPane } from "./levers-pane";
import { lockAvailableRaw, lockFundingSourceFor } from "./lock-funding";
import { expiryMsToTrialMinutes } from "./trial-duration";
import { deriveSettleCtaState, useSettleGate } from "./use-settle-gate";
import { useTrackFftSettleObserved } from "./use-track-fft-settle-observed";
import { useTrackFftTrialBlocked } from "./use-track-fft-trial-blocked";
import { useTrialPositionFlat } from "./use-trial-position-flat";

export interface FftDashboardProps {
  eligibility: Eligibility;
  activeTrial: TrialDto | null;
  usdcBalanceRaw: bigint;
  /** Primary-subaccount withdrawable USDC (raw) — undefined while the query is loading. */
  subaccountAvailableRaw: bigint | undefined;
  /** Owner address for the trial-history section; absent in override renders. */
  account?: string;
  /** Forces a step (overrides derivation). Used by Playwright/dev harness. */
  stepOverride?: FftStep;
  /** Suppresses analytics — set when rendering synthetic override state. */
  trackingDisabled?: boolean;
}

export function FftDashboard({
  eligibility,
  activeTrial,
  usdcBalanceRaw,
  subaccountAvailableRaw,
  account,
  stepOverride,
  trackingDisabled = false,
}: FftDashboardProps) {
  const locale = useLocale();
  const tLevers = useTranslations("firstTradeOnUs.levers");
  const tErrors = useTranslations("firstTradeOnUs.errors");
  const tGeneral = useTranslations("general");
  const { pendingLockAmount, setPendingLockAmount, pendingDurationDays, setPendingDurationDays } =
    useFftFlowStore();
  const lockMutation = useLockUsdc();
  const openTrialMutation = useOpenTrial();
  const claimMutation = useClaimUnlock();
  const settleTrialMutation = useSettleTrial();

  const derivedStep = stepOverride ?? deriveFftStep({ eligibility, activeTrial });

  // The previous trial's settled row is also truthy, so key on the pre-open row.
  const [pendingOpen, setPendingOpen] = useState<PendingOpen | null>(null);
  const awaitingOpen = isAwaitingOpen({ pendingOpen, activeTrial });
  if (pendingOpen !== null && !awaitingOpen) setPendingOpen(null);

  const queryClient = useQueryClient();
  useEffect(() => {
    if (!awaitingOpen || !openTrialMutation.isSuccess || !account) return;
    const interval = setInterval(() => {
      void readDexSdk.fundedFirstTrade
        .getActiveTrial({ account })
        .then((next) => {
          if (next === null) return;
          writeActiveTrial(queryClient, account, next);
          void queryClient.invalidateQueries({
            queryKey: fundedTrialEligibilityQueryKey({ account }),
          });
        })
        .catch(() => undefined);
    }, 3_000);
    return () => clearInterval(interval);
  }, [awaitingOpen, openTrialMutation.isSuccess, account, queryClient]);

  useEffect(() => {
    if (!awaitingOpen || !openTrialMutation.isSuccess) return;
    const timeout = setTimeout(() => {
      setPendingOpen(null);
      toast({
        variant: "error",
        title: tErrors("openTrialFailed"),
        description: tErrors("trialTimeout"),
      });
    }, 30_000);
    return () => clearTimeout(timeout);
  }, [awaitingOpen, openTrialMutation.isSuccess, tErrors]);

  const step = resolveFftStep({ derivedStep, awaitingOpen });
  // The cached row belongs to the previous trial until the new one lands.
  const visibleTrial = awaitingOpen ? null : activeTrial;

  // Warm the price subscription so data is already streaming by click time.
  useAllMarketPricesQuery();
  const historyQuery = useTrialHistoryInfiniteQuery({ account });
  // Preload the chart bundle once the user has a lock (staring at "Use Credit").
  const postLock = derivedStep !== "lock";
  useEffect(() => {
    if (postLock) preloadFftTrialChart();
  }, [postLock]);

  const lockCountdown = useLockCountdown(eligibility.activeLockUnlockAtMs);

  const minLockAmount = eligibility.minLockAmount;
  // The curve clamps at highLock — locking above it adds no protection.
  const maxLockAmount = eligibility.payoutAnchors.highLock;

  const creditsRemaining = eligibility.creditsGranted - eligibility.creditsUsed;
  const creditsTotal = eligibility.creditsGranted;
  const trialMinutes = expiryMsToTrialMinutes(eligibility.expiryMs);
  const isLockStep = step === "lock";
  const availableRaw = lockAvailableRaw(usdcBalanceRaw, subaccountAvailableRaw ?? BigInt(0));
  const balancesSettled = subaccountAvailableRaw !== undefined;

  useEffect(() => {
    if (
      balancesSettled &&
      isLockStep &&
      availableRaw >= minLockAmount &&
      pendingLockAmount > availableRaw
    ) {
      setPendingLockAmount(availableRaw);
    }
  }, [
    balancesSettled,
    isLockStep,
    availableRaw,
    minLockAmount,
    pendingLockAmount,
    setPendingLockAmount,
  ]);

  // Post-lock the levers display the locked config, not the pending cookie state.
  const displayAmount = isLockStep ? pendingLockAmount : eligibility.activeLockedAmount;
  const displayDuration = isLockStep
    ? pendingDurationDays
    : coerceDuration(eligibility.maxActiveDurationDays);
  const creditsExhausted = !isLockStep && step !== "completed" && creditsRemaining <= 0;
  const marketClosed = eligibility.blockerCodes.includes("market_not_open");
  const tradePositionUsd =
    Number(
      trialSizeFor(
        displayAmount,
        displayDuration,
        eligibility.payoutAnchors,
        eligibility.tierSlate,
      ),
    ) / USDC_SCALE;

  const claimable =
    eligibility.activeLockId !== null &&
    eligibility.activeLockUnlockAtMs !== null &&
    getServerTime() >= eligibility.activeLockUnlockAtMs;
  const onClaim = () => {
    if (eligibility.activeLockId === null || claimMutation.isPending) return;
    void claimMutation
      .mutateAsync({
        lockId: eligibility.activeLockId,
        lockedAmountUsd: Number(eligibility.activeLockedAmount) / USDC_SCALE,
        lockDurationDays: eligibility.maxActiveDurationDays,
        claimDelayMs:
          eligibility.activeLockUnlockAtMs !== null
            ? Math.max(0, getServerTime() - eligibility.activeLockUnlockAtMs)
            : undefined,
      })
      .catch((e: unknown) => {
        if (isFftLockHasOpenPositionError(e)) {
          toast({
            variant: "error",
            title: tErrors("claimFailed"),
            description: tErrors("lockHasOpenPosition"),
          });
          return;
        }
        if (isFftLockHasOpenOrdersError(e)) {
          toast({
            variant: "error",
            title: tErrors("claimFailed"),
            description: tErrors("lockHasOpenOrders"),
          });
          return;
        }
        toast({
          variant: "error",
          title: tErrors("claimFailed"),
          description: e instanceof Error ? e.message : "",
        });
      });
  };

  const isUnlockCta = !isLockStep && claimable;
  const ctaLabel = isLockStep
    ? tLevers("ctaLock")
    : isUnlockCta
      ? tLevers("ctaUnlock")
      : tLevers("ctaLocked");
  const fundingSource = lockFundingSourceFor(
    pendingLockAmount,
    usdcBalanceRaw,
    subaccountAvailableRaw ?? BigInt(0),
  );

  const ctaLoading = isLockStep ? lockMutation.isPending : claimMutation.isPending;
  const ctaDisabled = isUnlockCta
    ? claimMutation.isPending
    : !isLockStep ||
      eligibility.locksPaused ||
      pendingLockAmount < minLockAmount ||
      pendingLockAmount > availableRaw ||
      lockMutation.isPending;

  const onCtaClick = () => {
    if (isUnlockCta) {
      onClaim();
      return;
    }
    if (!isLockStep) return;
    void lockMutation
      .mutateAsync({
        amount: pendingLockAmount,
        durationDays: pendingDurationDays,
        fundingSource,
        projectedCredits: creditSlateForDurationDays(pendingDurationDays, eligibility.tierSlate)
          .credits,
        source: "dashboard",
      })
      .catch((e: unknown) => {
        if (isFftRelockDisabledError(e)) {
          toast({
            variant: "error",
            title: tErrors("lockFailed"),
            description: tErrors("relockDisabled"),
          });
          return;
        }
        if (isFftLockWithdrawalQueuedError(e)) {
          Logger.error(e, { message: "FFT lock aborted: withdrawal queued", feature: "fft" });
          toast({
            variant: "error",
            title: tErrors("lockFailed"),
            description: tErrors("lockWithdrawalQueued"),
          });
          return;
        }
        toast({
          variant: "error",
          title: tErrors("lockFailed"),
          description: e instanceof Error ? e.message : "",
        });
      });
  };

  // Lock creation isn't surfaced directly — recover it from the on-chain unlock timestamp.
  const lockCreatedAtMs =
    eligibility.activeLockUnlockAtMs !== null
      ? eligibility.activeLockUnlockAtMs - eligibility.maxActiveDurationDays * MS_PER_DAY
      : null;

  const onUseCredit = (source: FftTrialOpenSource) => {
    if (!eligibility.canOpenTrial || openTrialMutation.isPending) return;
    setPendingOpen({ priorTrialKey: trialKey(activeTrial) });
    void openTrialMutation
      .mutateAsync({
        source,
        creditsRemaining,
        creditsTotal,
        lockedAmountUsd: Number(eligibility.activeLockedAmount) / USDC_SCALE,
        timeFromLockMs:
          lockCreatedAtMs !== null ? Math.max(0, getServerTime() - lockCreatedAtMs) : undefined,
      })
      .catch((e: unknown) => {
        setPendingOpen(null);
        toast({
          variant: "error",
          title: tErrors("openTrialFailed"),
          description: e instanceof Error ? e.message : "",
        });
      });
  };

  // Failures skip the hold (only success records a submission) so retry stays immediate.
  const [settleSubmitted, setSettleSubmitted] = useState<{
    trialId: number;
    atMs: number;
  } | null>(null);
  const settleSubmittedAtMs =
    settleSubmitted !== null && settleSubmitted.trialId === activeTrial?.trial_id
      ? settleSubmitted.atMs
      : null;
  const closeAttempted = settleSubmittedAtMs !== null || (activeTrial?.close_stalled ?? false);
  const settle =
    activeTrial !== null
      ? {
          pending: settleTrialMutation.isPending,
          resubmit: closeAttempted,
          onSettle: () => {
            if (settleTrialMutation.isPending) return;
            const trialId = activeTrial.trial_id;
            void settleTrialMutation
              .mutateAsync({
                trialId,
                overdueMs:
                  activeTrial.expires_at_ms !== undefined
                    ? Math.max(0, getServerTime() - activeTrial.expires_at_ms)
                    : undefined,
              })
              .then(() => setSettleSubmitted({ trialId, atMs: getServerTime() }))
              .catch((e: unknown) => {
                toast({
                  variant: "error",
                  title: tErrors("settleFailed"),
                  description: e instanceof Error ? e.message : "",
                });
              });
          },
        }
      : undefined;

  const settleCtaVisible = useSettleGate({ trial: activeTrial, settleSubmittedAtMs });
  const positionFlat = useTrialPositionFlat(activeTrial);
  const settleCtaState = deriveSettleCtaState({
    gateOpen: settleCtaVisible,
    closeAttempted,
    positionFlat,
  });
  const gatedSettle = settleCtaState === "cta" ? settle : undefined;
  const settleStalled = settleCtaState === "stalled";

  const bodyVariant = pickBodyVariant({ step, activeTrial: visibleTrial });

  const trackingEnabled = !trackingDisabled;
  useTrackFftTrialBlocked({ eligibility, step, enabled: trackingEnabled });
  useTrackFftSettleObserved({
    trial: activeTrial,
    manualSettle: settleTrialMutation.isSuccess,
    enabled: trackingEnabled,
  });

  // One URL serves four funnel stages — the auto pageview can't see `step`.
  const analytics = useAnalytics();
  const lastViewedStep = useRef<FftStep | null>(null);
  useEffect(() => {
    if (!trackingEnabled || !analytics.rudder || lastViewedStep.current === step) return;
    lastViewedStep.current = step;
    analytics.fft.dashboardViewed({ step, bodyVariant });
  }, [trackingEnabled, analytics, step, bodyVariant]);

  const profitsKeptUsd = useMemo(
    () =>
      deriveProfitsKeptUsd({
        activeTrial,
        campaignAddr: sdkConfig.deployment.fftCampaignAddr,
        historyPages: historyQuery.data?.pages,
      }),
    [activeTrial, historyQuery.data?.pages],
  );

  const metrics =
    step === "lock" || step === "completed"
      ? null
      : {
          locked: NumberFormatter.usdc(Number(eligibility.activeLockedAmount) / USDC_SCALE),
          unlocksAt:
            eligibility.activeLockUnlockAtMs === null
              ? tGeneral("loadingPlaceholder")
              : formatUnlockDate(eligibility.activeLockUnlockAtMs, locale),
          creditsRemaining,
          creditsTotal,
          profitsKeptUsd,
        };

  return (
    <div
      className={flex({
        gap: "16",
        // Two-pane only from lg — the positions table + chart need the room.
        direction: { base: "column", lg: "row" },
        w: "full",
        flex: "1",
        align: "stretch",
      })}
      data-testid="fft-dashboard"
      data-step={step}
    >
      <LeversPane
        amount={displayAmount}
        onAmountChange={(amount) => {
          const capped = amount > availableRaw ? availableRaw : amount;
          setPendingLockAmount(capped);
        }}
        minLockAmount={minLockAmount}
        maxLockAmount={maxLockAmount}
        durationDays={displayDuration}
        onDurationChange={(n) => setPendingDurationDays(coerceDuration(n))}
        tierSlate={eligibility.tierSlate}
        creditsRemaining={
          isLockStep
            ? creditSlateForDurationDays(pendingDurationDays, eligibility.tierSlate).credits
            : creditsRemaining
        }
        creditsTotal={
          isLockStep
            ? creditSlateForDurationDays(pendingDurationDays, eligibility.tierSlate).credits
            : creditsTotal
        }
        tradePositionUsd={tradePositionUsd}
        ctaLabel={ctaLabel}
        ctaDisabled={ctaDisabled}
        ctaLoading={ctaLoading}
        onCtaClick={onCtaClick}
        trialMinutes={trialMinutes}
        fundingSource={isLockStep ? fundingSource : undefined}
        leversDisabled={!isLockStep}
        alert={
          creditsExhausted ? (
            <FftCreditsUsedAlert lockCountdown={lockCountdown} />
          ) : marketClosed ? (
            <FftMarketClosedAlert preLock={isLockStep} />
          ) : undefined
        }
      />
      <DetailsPane
        header={{
          // Covers stale eligibility: an open in another tab leaves canOpenTrial true until the WS reconcile lands.
          useCreditDisabled:
            step === "lock" ||
            awaitingOpen ||
            activeTrial?.status === "Active" ||
            !eligibility.canOpenTrial ||
            openTrialMutation.isPending,
          useCreditLoading: openTrialMutation.isPending || awaitingOpen,
          onUseCredit: () => onUseCredit("use_credit"),
          creditsExhausted,
          settleNow: gatedSettle,
        }}
        metrics={metrics}
        bodyVariant={bodyVariant}
        trialMinutes={trialMinutes}
        marketAddr={eligibility.marketAddr}
        account={account}
        activeTrial={visibleTrial ?? undefined}
        lockCountdown={lockCountdown}
        claim={{ claimable, pending: claimMutation.isPending, onClaim }}
        settle={gatedSettle}
        positionFlat={positionFlat}
        settleStalled={settleStalled}
      />
    </div>
  );
}

function pickBodyVariant({
  step,
}: {
  step: FftStep;
  activeTrial: TrialDto | null;
}): DetailsBodyVariant {
  switch (step) {
    case "lock":
    case "completed":
      return "pre-lock";
    case "trial":
      return "post-lock-empty";
    case "active":
      return "active-trial";
    case "settled":
      return "settled-trial";
  }
}

function coerceDuration(n: number): LockDurationDays {
  return isLockDurationDays(n) ? n : 7;
}

function formatUnlockDate(unlockAtMs: number, locale: string): string {
  return new Date(unlockAtMs).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
